import { AlertTriangle, Download } from 'lucide-react'
import { cn, formatCompactNumber } from '@/lib/utils'
import type { ContextSection, ContextSnapshot } from '@/types/contextObservability'
import { SECTION_COLORS } from '@/types/contextObservability'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { downloadContextSnapshot } from '@/api/contextObservability'

export interface ContextBudgetPanelProps {
  snapshot: ContextSnapshot
  className?: string
}

export function ContextBudgetPanel({ snapshot, className }: ContextBudgetPanelProps) {
  const totalUsed = snapshot.sections.reduce((sum, s) => sum + s.tokens, 0)
  const headroom = snapshot.contextWindowSize - totalUsed
  const usedPct = snapshot.contextWindowSize > 0
    ? (totalUsed / snapshot.contextWindowSize) * 100
    : 0
  const thresholdTokens = Math.round(
    snapshot.contextWindowSize * (snapshot.summarizeThresholdPct / 100)
  )
  const isNearThreshold = totalUsed > thresholdTokens * 0.8

  return (
    <Card className={cn('sticky top-4', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Context Budget</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <DonutChart
          sections={snapshot.sections}
          usedPct={usedPct}
        />

        <div className="space-y-1.5">
          {snapshot.sections.filter(s => s.tokens > 0).map((section) => (
            <div key={section.key} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: SECTION_COLORS[section.key] ?? section.color }}
                />
                <span className="text-muted-foreground">{section.label}</span>
              </span>
              <span className="font-mono">{formatCompactNumber(section.tokens)}</span>
            </div>
          ))}
        </div>

        <Separator />

        <div className="space-y-1.5 text-xs">
          <VitalRow label="Context window" value={formatCompactNumber(snapshot.contextWindowSize)} />
          <VitalRow label="Used" value={formatCompactNumber(totalUsed)} />
          <VitalRow label="Headroom" value={formatCompactNumber(headroom)} />
          <VitalRow
            label="Summarize at"
            value={`${snapshot.summarizeThresholdPct}% · ${formatCompactNumber(thresholdTokens)}`}
          />
          <VitalRow label="Model" value={snapshot.modelName} />
        </div>

        {isNearThreshold && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Approaching summarize threshold. Earlier messages will be compressed on the next turn.
            </span>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => downloadContextSnapshot(snapshot.snapshotId)}
        >
          <Download className="h-4 w-4 mr-2" />
          Export JSON
        </Button>
      </CardContent>
    </Card>
  )
}

function VitalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-medium">{value}</span>
    </div>
  )
}

function DonutChart({
  sections,
  usedPct,
}: {
  sections: ContextSection[]
  usedPct: number
}) {
  const size = 140
  const strokeWidth = 20
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  const nonZeroSections = sections.filter(s => s.tokens > 0)
  const totalTokens = nonZeroSections.reduce((sum, s) => sum + s.tokens, 0)

  let cumulativeOffset = 0
  const arcs = nonZeroSections.map((section) => {
    const pct = totalTokens > 0 ? section.tokens / totalTokens : 0
    const dashLength = pct * circumference
    const dashOffset = circumference - cumulativeOffset
    cumulativeOffset += dashLength
    return {
      key: section.key,
      color: SECTION_COLORS[section.key] ?? section.color,
      dashArray: `${dashLength} ${circumference - dashLength}`,
      dashOffset,
    }
  })

  return (
    <div className="flex justify-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth={strokeWidth}
          />
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={arc.dashArray}
              strokeDashoffset={arc.dashOffset}
              strokeLinecap="butt"
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold">{usedPct.toFixed(0)}%</span>
          <span className="text-[10px] text-muted-foreground">used</span>
        </div>
      </div>
    </div>
  )
}
